import { AppError, type AppErrorCode, type AppErrorPolicy, isAppError } from "./app-error";

/**
 * §5.3 / §5.7 / §5.8.3: 解析 Worker と main thread の通信プロトコル。
 *
 * 1 Worker 構成 + in-flight 1 件直列化 (id による correlation は採用しない)。
 * `processFrame` の RTT が 33ms 以内なら 30fps を維持でき、`updateSettings` 等の
 * 制御 RPC は frame の合間に直列に挟まれる。
 *
 * Worker → main の応答は `WorkerResult<T>` (`{ ok, code, policy, message?, value? }`) を
 * そのまま流す。 main 側は `workerResultToError` で AppError へ復元してから呼び出し元に
 * throw するため、呼び出し側からは「普通に throw する関数」に見える。 例外は postMessage を
 * 越えられないため、 Worker 側は throw せず `errorToWorkerResult` で Result に詰めて返す。
 * (AppError クラスと `AppErrorCode` / `AppErrorPolicy` の定義は `app-error.ts`。)
 *
 * issue #130: audio VAD (Silero / RMS) は撤去された。 worker は face landmark 抽出 + raw signal
 * (blendshapes / headPose / lipLandmarks) を返すのみ。 発話判定 (`effectiveIsSpeaking`) は main 側
 * `features/session/lib/vision-vad` で raw 2D MAR (`frame.mar`) Δ の rolling 90 frame mean から
 * 決定する (ADR 0003 update / F32、 issue #204。 ARKit `blendshapes.jawOpen` の ML 推定 noise を
 * 避けるため raw 2D MAR の純粋幾何 signal に切替済)。
 */

/** §5.8.3: Worker 境界で送る plain object 表現 (例外は postMessage を越えられない)。 */
export type WorkerResult<T> =
  | { ok: true; value: T }
  | { ok: false; code: AppErrorCode; policy: AppErrorPolicy; message?: string };

/** main 側: 受け取った Result を AppError に復元 (失敗時 throw)。 呼び出し元には普通の関数に見える。 */
export function workerResultToError<T>(result: WorkerResult<T>): T {
  if (result.ok) return result.value;
  throw new AppError(result.code, result.policy, result.message);
}

/** Worker 側: 例外を Result に詰めて返す (postMessage 用)。 非 AppError は Unexpected/transient に丸める。 */
export function errorToWorkerResult(err: unknown): Extract<WorkerResult<never>, { ok: false }> {
  if (isAppError(err)) {
    return { ok: false, code: err.code, policy: err.policy, message: err.message };
  }
  return {
    ok: false,
    code: "Unexpected",
    policy: "transient",
    message: err instanceof Error ? err.message : String(err),
  };
}

export type WorkerInitPayload = {
  mediapipeBaseUrl: string;
};

export type WorkerRequest =
  | { type: "ping" }
  | { type: "init"; payload: WorkerInitPayload }
  | {
      type: "process-frame";
      // VideoFrame は transferable で zero-copy 受け渡しする (4.1)。
      payload: { videoFrame: VideoFrame; timestamp: number };
    }
  | { type: "update-settings"; payload: { thresholdC: number; cooldownMs: number } };

export type AnalyzeFrameResult = {
  mar: number;
  /**
   * issue #163 / ADR 0004 (F24 production 反映): mar は landmark 4 点 (13/14/61/291) の x/y のみ
   * から計算された 2D MAR。 yaw 大の frame で 2D 距離は z 込みの 3D 距離より縦/横方向で異なる
   * 比率で膨らみ、 結果 MAR が 1.06〜1.47 倍まで飽和する (F19 訂正版)。 mar3D は同 4 点で z も
   * 含めた Euclidean 距離で再計算した MAR で、 main 側 `mar-3d-ratio` helper が
   * `effectiveMar = clamp(normalizedMar * (mar3D / mar2D), 0, 1)` を組み立てる。 worker が
   * raw scalar を返し、 ratio compute は main 側に閉じる構成 (CLAUDE.md §worker = signal extractor、
   * main = stateful decision maker と整合)。 顔未検出フレームは undefined。
   */
  mar3D?: number;
  faceDetected: boolean;
  timestamp: number;
  /**
   * detection-tuning v2 (issue #109, #108 enabler): MediaPipe FaceLandmarker の ARKit 系
   * blendshapes から curated サブセット (~12) を categoryName キーで抽出した score (0..1) 記録。
   * yawn / smile は唇幾何 (MAR) では TP と同形だが blendshapes では別物 — 目の細め・眉・
   * 口角・jawOpen が弁別 signal になる。 顔未検出フレームは undefined。
   *
   * 主用途:
   * - production `features/session/lib/smile-blendshape-gate` が tight 閾値 (mouthSmile 0.3 /
   *   cheekSquint 0.2) で `suppressFire` を立てる (ADR 0004 / F24。 消費は issue#283 で B 層 openSince reset 要因)
   * - dev-only `features/eval` の main 側 candidate-aware transformation 層の input
   *   (= `vision-vad-v1` 等の eval transform が `jawOpen` 時系列を入力にする)。 production の
   *   vision-VAD は F32 以後 raw 2D MAR Δ 経路に移行し blendshapes を読まない (issue #204)
   */
  blendshapes?: Record<string, number>;
  /**
   * detection-tuning v2 (issue #109, #108 enabler): MediaPipe facial transformation matrix
   * (4×4) から導出した頭部姿勢 (pitch / yaw / roll、 単位は度)。 landmark 座標の概算より
   * 桁違いに正確なため face-orientation gating の変種比較がまともに回る。 顔未検出フレームは undefined。
   *
   * 主用途: #108 の main 側 candidate-aware transformation 層の input。 issue #286 (pose-reset lever /
   * ADR 0013) 以降は production 経路も `yaw` を消費する (detection-loop の pose-reset-monitor →
   * dynamic-open-baseline 窓 reset)。 pitch / roll は引き続き eval 専用。
   */
  headPose?: { pitch: number; yaw: number; roll: number };
  /**
   * detection-tuning v2 (issue #109, #108 enabler): 内唇コンタ + 口角の唇 landmark クラスタ
   * を MediaPipe canonical index をキーに持つ x/y/z 座標記録。 worker は mar スカラしか返さない
   * ため main は MAR 計算式の変種をスカラから再現できない — クラスタを流すことで任意の MAR
   * 変種を main 側 pure 関数にできる。 現行 mar-calculator が使う 4 点 (13/14/61/291) を包含する
   * superset。 478 点全ては流さず唇周辺のみ。 顔未検出 / 必須 index 欠落フレームは undefined。
   *
   * 主用途: #108 の main 側 candidate-aware transformation 層の input。 production 経路は読まない。
   */
  lipLandmarks?: Record<number, { x: number; y: number; z: number }>;
};

export type WorkerResponse =
  | { method: "ping"; result: WorkerResult<"pong"> }
  | { method: "init"; result: WorkerResult<null> }
  | { method: "process-frame"; result: WorkerResult<AnalyzeFrameResult> }
  | { method: "update-settings"; result: WorkerResult<null> };

/**
 * AnalyzeFrameResult schema の整数 version (= 破壊変更の度に手動 bump)。 規律詳細は
 * docs/adr/0005-frame-dump-cache.md。 worker が出力する AnalyzeFrameResult shape を破壊変更
 * (field 追加 / 削除 / 型変更 / 計算式変更で同入力に対する出力が変わる) する PR は、 必ず
 * 1 commit に以下を含める:
 *   1. OUTPUT_SCHEMA_VERSION の +1 bump
 *   2. pnpm dump-frames --force で全 dump 再生成
 *   3. corpus/raw-frames/*.json.gz の更新を同 PR に含める
 * bump 忘れ + dump 再生成 忘れの両方が同時に起きた事故のみ stale を見落とす。 (1) bump しか
 * しなかった場合は eval load 時の fail-fast で気付ける = primary 防衛線。
 *
 * 初期値 3 は ADR 0005 §決定 §OUTPUT_SCHEMA_VERSION 規律 で示唆された値で、 過去の schema
 * 拡張 [#109 blendshapes / headPose / lipLandmarks 追加, #130 audio VAD 撤去,
 * #133 frame-budget, F24 mar3D 追加] の積算が根拠 (絶対値は意味を持たない、 mismatch 検出だけが目的)。
 */
export const OUTPUT_SCHEMA_VERSION = 4;
